import { FC, ReactNode } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from '../hooks/useAuth';
import { emailVerifiedRedirect } from './emailVerifiedRedirect';
import {
  clearPostLoginRedirect,
  setPostLoginRedirect,
} from './postLoginRedirect';

interface SignedInGuardProps {
  children?: ReactNode;
}

const SignedInGuard: FC<SignedInGuardProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Stash where the user was headed (path + query) so login can return them
    // here. Without this a deep link behind the guard — notably the MCP consent
    // page /mcp/consent?request=… — is lost on sign-in: the user lands on the
    // dashboard and the OAuth authorization silently dead-ends.
    //
    // Stored in BOTH places on purpose: React Router history state is the fast
    // path, but it is in-memory and is wiped by a full page reload and by the
    // Firebase auth re-init that gates the app on boot — the actual reason the
    // consent request was getting lost. sessionStorage survives both; GuestGuard
    // consumes whichever is present.
    const from = location.pathname + location.search;
    setPostLoginRedirect(from);
    return <Redirect to={{ pathname: '/login', state: { from } }} />;
  }

  const verifyRedirect = emailVerifiedRedirect(user);
  if (verifyRedirect) {
    // Bouncing an authenticated-but-unverified user to /verify-email: re-stash
    // the destination so the deep link (e.g. the MCP consent page) survives the
    // verification detour instead of dead-ending on the dashboard after it.
    setPostLoginRedirect(location.pathname + location.search);
    return verifyRedirect;
  }

  // The user has ARRIVED on the guarded destination — consume the stash now
  // (idempotent; only clears when it matches this exact path+query). Arrival-
  // based consumption means a competing redirect elsewhere in the tree can
  // never burn the deep link before it was honored.
  clearPostLoginRedirect(location.pathname + location.search);

  return <>{children}</>;
};

SignedInGuard.propTypes = {
  children: PropTypes.node,
};

export default SignedInGuard;
